// Homegrown Shop (web) — 1:1 mirror of the mobile boutique (app/shop.tsx).
// Same brand tokens, member-discount hint, Recommended-for-You rail, wishlist,
// aesthetic product grid with a hand-drawn −20% member badge, floating cart bar,
// hero-zoom product detail with a segmented format selector, a single-page
// checkout sheet with an Apple Pay / Express-Pay button, and a keyboard-safe
// shipping form. Backend contract shared with mobile — untouched.
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '@/components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingBag, Heart, Clock, Plus, Minus, X, Check, Star, Truck, Download, Gift,
  BookOpen, Zap, ChevronDown, ArrowRight, Lock, PiggyBank, AlertCircle, CheckCircle2, Loader2,
} from 'lucide-react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const C = {
  bg: '#E5EBDD', sageDeep: '#D9E1CB', cream: '#FAF6EC', creamWarm: '#F4EEDE',
  forest: '#1A4331', forestDeep: '#0F2D1F', gold: '#C9A85B', goldTint: 'rgba(201,168,91,0.14)',
  terracotta: '#C66B4A', terracottaTint: 'rgba(198,107,74,0.12)',
  leaf: '#6B9F4A', leafDeep: '#4E7A33', leafTint: 'rgba(107,159,74,0.14)',
  text: '#2A3528', textMuted: '#5C665C', textSoft: '#7A6B57', border: 'rgba(26,67,49,0.10)',
};
const money = (sym, n) => `${sym}${Number(n || 0).toFixed(2)}`;

const AppleLogo = ({ size = 18 }) => (
  <svg viewBox="0 0 384 512" width={size} height={size} fill="currentColor" aria-hidden="true" style={{ marginTop: -2 }}>
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

// ---- Hand-drawn "member discount" badge (dashed motif + slight tilt) ----
function SketchyBadge({ pct }) {
  return (
    <span data-testid="sketchy-member-badge"
      className="inline-flex items-center gap-1 rounded-xl px-2 py-1 text-[11px] font-bold"
      style={{ background: C.gold, color: C.forest, border: '1.5px dashed #8A6D2B', transform: 'rotate(-4deg)', boxShadow: '0 6px 16px -8px rgba(26,67,49,0.35)' }}>
      <Star className="w-2.5 h-2.5" /> Member −{pct}%
    </span>
  );
}

function GridPrice({ p, sym }) {
  const showStrike = p.is_premium && p.discount_eligible && p.min_price < p.min_price_original;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[15px] font-bold" style={{ color: C.forest }}>{p.has_variants ? `From ${money(sym, p.min_price)}` : money(sym, p.min_price)}</span>
      {showStrike && <span className="text-xs line-through" style={{ color: C.textMuted }}>{money(sym, p.min_price_original)}</span>}
    </div>
  );
}

function CardTag({ p }) {
  if (p.is_gift_card) return <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold" style={{ background: C.gold, color: C.forest }}><Gift className="w-2.5 h-2.5" /> Gift</span>;
  if (p.category === 'books') return <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold" style={{ background: 'rgba(26,67,49,0.9)', color: C.cream }}><BookOpen className="w-2.5 h-2.5" /> 2 formats</span>;
  return null;
}

function HeartBtn({ saved, onClick, testId }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} data-testid={testId}
      className="w-8 h-8 rounded-full flex items-center justify-center transition-transform active:scale-90"
      style={{ background: 'rgba(250,246,236,0.92)', boxShadow: '0 6px 16px -8px rgba(26,67,49,0.35)' }}>
      <Heart className="w-4 h-4" style={{ color: saved ? C.terracotta : C.forest, fill: saved ? C.terracotta : 'none' }} />
    </button>
  );
}

// ---- Uber-style wide rounded-pill button (matches mobile PayPill) ----
function PayPill({ label, icon: Icon, apple, tone = 'forest', onClick, loading, testId }) {
  const bg = apple || tone === 'apple' ? '#111111' : tone === 'ghost' ? C.creamWarm : C.forest;
  const fg = tone === 'ghost' ? C.forest : C.cream;
  return (
    <button onClick={onClick} disabled={loading} data-testid={testId}
      className="w-full h-14 rounded-full flex items-center justify-center gap-2 font-semibold text-base transition-transform active:scale-[0.98] disabled:opacity-70"
      style={{ background: bg, color: fg, boxShadow: '0 10px 24px -10px rgba(26,67,49,0.45)' }}>
      {loading ? <Loader2 className="w-5 h-5 animate-spin" />
        : (<>{apple ? <AppleLogo size={19} /> : Icon ? <Icon className="w-[17px] h-[17px]" /> : null}<span>{label}</span></>)}
    </button>
  );
}

function Stepper({ qty, onAdd, onDec, id }) {
  if (qty <= 0) {
    return (
      <button onClick={onAdd} data-testid={`variant-add-${id}`} className="inline-flex items-center gap-1 rounded-full px-4 py-2.5 font-semibold text-sm transition-transform active:scale-95" style={{ background: C.forest, color: C.cream }}>
        <Plus className="w-3.5 h-3.5" /> Add
      </button>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-full px-2 py-1.5" style={{ background: C.leafTint }}>
      <button onClick={onDec} data-testid={`qty-dec-${id}`} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: C.cream }}><Minus className="w-3.5 h-3.5" style={{ color: C.forest }} /></button>
      <span className="font-bold min-w-[16px] text-center" style={{ color: C.forest }}>{qty}</span>
      <button onClick={onAdd} data-testid={`qty-inc-${id}`} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: C.cream }}><Plus className="w-3.5 h-3.5" style={{ color: C.forest }} /></button>
    </div>
  );
}

function ProductCard({ p, sym, isPremium, qtyInCart, saved, onToggleSave, onOpen, index }) {
  const showBadge = isPremium && p.discount_eligible;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ type: 'spring', stiffness: 220, damping: 24, delay: Math.min(index, 8) * 0.03 }}
      whileHover={{ y: -4 }} whileTap={{ scale: 0.98 }}
      role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }} data-testid={`shop-product-${p.id}`}
      className="text-left rounded-2xl overflow-hidden w-full group cursor-pointer"
      style={{ background: C.cream, boxShadow: '0 14px 40px -18px rgba(26,67,49,0.4)' }}>
      <div className="relative">
        <img src={p.image} alt={p.name} loading="lazy" className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" style={{ aspectRatio: '1 / 1', background: C.sageDeep }} />
        <CardTag p={p} />
        <div className="absolute bottom-2.5 right-2.5"><HeartBtn saved={saved} onClick={onToggleSave} testId={`wishlist-heart-${p.id}`} /></div>
        {showBadge && <div className="absolute top-2.5 right-2.5"><SketchyBadge pct={p.discount_pct} /></div>}
        {qtyInCart > 0 && <span className="absolute bottom-2.5 left-2.5 rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: C.cream, color: C.forest, boxShadow: '0 6px 16px -8px rgba(26,67,49,0.35)' }}>{qtyInCart} in cart</span>}
      </div>
      <div className="p-3">
        <p className="font-semibold truncate" style={{ color: C.text }}>{p.name}</p>
        <div className="mt-1"><GridPrice p={p} sym={sym} /></div>
      </div>
    </motion.div>
  );
}

function RecoCard({ p, sym, isPremium, saved, onToggleSave, onOpen }) {
  const showBadge = isPremium && p.discount_eligible;
  return (
    <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }} data-testid={`reco-card-${p.id}`}
      className="flex-shrink-0 w-[210px] rounded-2xl overflow-hidden text-left transition-transform active:scale-[0.98] cursor-pointer"
      style={{ background: C.cream, boxShadow: '0 14px 40px -18px rgba(26,67,49,0.4)' }}>
      <div className="relative">
        <img src={p.image} alt={p.name} className="w-full object-cover" style={{ height: 130, background: C.sageDeep }} />
        <div className="absolute bottom-2.5 right-2.5"><HeartBtn saved={saved} onClick={onToggleSave} testId={`wishlist-heart-${p.id}`} /></div>
        {showBadge && <div className="absolute top-2.5 right-2.5"><SketchyBadge pct={p.discount_pct} /></div>}
      </div>
      {p.reason && (
        <div className="flex items-start gap-1.5 px-2 py-2" style={{ background: C.goldTint, minHeight: 40 }}>
          <Zap className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: C.gold }} />
          <span className="text-[11px] font-semibold leading-snug line-clamp-2" style={{ color: C.forest }}>{p.reason}</span>
        </div>
      )}
      <div className="p-2.5">
        <p className="font-semibold truncate" style={{ color: C.text }}>{p.name}</p>
        <div className="mt-1"><GridPrice p={p} sym={sym} /></div>
      </div>
    </div>
  );
}

export default function Shop({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([{ code: 'all', label: 'All' }]);
  const [activeCat, setActiveCat] = useState('all');
  const [recos, setRecos] = useState([]);
  const [recoCtx, setRecoCtx] = useState(null);
  const [cart, setCart] = useState([]);
  const [detail, setDetail] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [banner, setBanner] = useState(null);
  const [wishlist, setWishlist] = useState(new Set());
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const [showWishlist, setShowWishlist] = useState(false);

  const catalog = useMemo(() => {
    const m = {}; [...products, ...recos, ...wishlistProducts].forEach((p) => { m[p.id] = p; }); return m;
  }, [products, recos, wishlistProducts]);
  const sym = products[0]?.currency_symbol || recos[0]?.currency_symbol || '$';
  const isPremium = products[0]?.is_premium ?? recos[0]?.is_premium ?? !!user?.subscription_active;

  const loadProducts = (cat) => axios.get(`${API}/shop/products`, { params: cat && cat !== 'all' ? { category: cat } : {}, withCredentials: true })
    .then((r) => { setProducts(r.data.products || []); if (r.data.categories) setCats(r.data.categories); }).catch(() => {});
  const loadRecos = () => axios.get(`${API}/shop/recommendations`, { withCredentials: true })
    .then((r) => { setRecos(r.data.items || []); setRecoCtx(r.data.context || null); }).catch(() => {});
  const loadWishlist = () => axios.get(`${API}/shop/wishlist`, { withCredentials: true })
    .then((r) => { setWishlist(new Set(r.data.product_ids || [])); setWishlistProducts(r.data.products || []); }).catch(() => {});

  useEffect(() => { Promise.all([loadProducts('all'), loadRecos(), loadWishlist()]).finally(() => setLoading(false)); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!loading) loadProducts(activeCat); }, [activeCat]);

  // Return from Stripe hosted checkout → confirm payment.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (params.get('purchase') === 'cancelled') setBanner({ type: 'error', text: 'Checkout cancelled — your cart is safe.' });
    if (sid) {
      setBanner({ type: 'ok', text: 'Confirming your payment…' });
      const poll = async (n = 0) => {
        try {
          const r = await axios.get(`${API}/shop/checkout/status/${sid}`, { withCredentials: true });
          if (r.data.payment_status === 'paid') {
            const digital = r.data.has_digital || r.data.has_gift_card;
            setBanner({ type: 'ok', library: digital, text: digital ? '🎉 Order confirmed! Your digital items are in your Library.' : '🎉 Order confirmed! Thanks for supporting your garden.' });
            setCart([]); return;
          }
          if (n < 8) setTimeout(() => poll(n + 1), 2000);
          else setBanner({ type: 'error', text: 'Still confirming — check My Orders shortly.' });
        } catch { if (n < 8) setTimeout(() => poll(n + 1), 2000); }
      };
      poll();
      window.history.replaceState({}, '', '/shop');
    }
  }, []);

  const toggleWishlist = async (pid) => {
    const saved = wishlist.has(pid);
    setWishlist((s) => { const n = new Set(s); if (saved) n.delete(pid); else n.add(pid); return n; });
    const r = await axios.post(`${API}/shop/wishlist/${pid}`, {}, { withCredentials: true }).catch(() => null);
    if (r && typeof r.data.saved === 'boolean') loadWishlist();
  };

  const cartCount = cart.reduce((n, i) => n + i.qty, 0);
  const cartTotal = cart.reduce((s, i) => s + i.variant.effective_price * i.qty, 0);
  const needsShipping = cart.some((i) => i.variant.requires_shipping);
  const productQty = (pid) => cart.filter((i) => i.product.id === pid).reduce((a, i) => a + i.qty, 0);

  const addLine = (product, variant, n = 1) => { setBanner(null); setCart((prev) => {
    const idx = prev.findIndex((i) => i.product.id === product.id && i.variant.id === variant.id);
    if (idx >= 0) { const next = [...prev]; next[idx] = { ...next[idx], qty: next[idx].qty + n }; return next; }
    return [...prev, { product, variant, qty: n }];
  }); };
  const decLine = (product, variant) => setCart((prev) => {
    const idx = prev.findIndex((i) => i.product.id === product.id && i.variant.id === variant.id);
    if (idx < 0) return prev; const q = prev[idx].qty - 1;
    if (q <= 0) return prev.filter((_, k) => k !== idx);
    const next = [...prev]; next[idx] = { ...next[idx], qty: q }; return next;
  });

  const onCheckoutPress = () => {
    if (!cart.length) return;
    if (needsShipping) { setCartOpen(false); setShipOpen(true); } else doCheckout(null);
  };
  const doCheckout = async (shipping) => {
    setCheckingOut(true); setBanner(null);
    try {
      const r = await axios.post(`${API}/shop/checkout`, {
        items: cart.map((i) => ({ product_id: i.product.id, variant_id: i.variant.id, quantity: i.qty })),
        origin_url: window.location.origin, shipping_address: shipping || null,
      }, { withCredentials: true });
      if (!r.data.url) throw new Error('no url');
      window.location.href = r.data.url;
    } catch (e) {
      setBanner({ type: 'error', text: e.response?.data?.detail || 'Could not start checkout. Please try again.' });
      setCheckingOut(false);
    }
  };

  const gridProducts = showWishlist ? wishlistProducts : products;

  return (
    <Layout user={user}>
      <div className="space-y-5 pb-28">
        {/* Header */}
        <div data-testid="shop-hero" className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: C.textMuted }}>The Boutique Shop</p>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>Grower&apos;s Store</h1>
            <p className="mt-1" style={{ color: C.textMuted }}>Curated for your garden</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowWishlist((v) => !v)} data-testid="shop-wishlist-button"
              className="relative w-10 h-10 rounded-full flex items-center justify-center transition-transform active:scale-90"
              style={{ background: showWishlist ? C.terracottaTint : C.creamWarm }}>
              <Heart className="w-[18px] h-[18px]" style={{ color: showWishlist ? C.terracotta : C.forest, fill: showWishlist ? C.terracotta : 'none' }} />
              {wishlist.size > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: C.terracotta, color: C.cream }}>{wishlist.size}</span>}
            </button>
            <Link to="/orders" data-testid="shop-orders-link" className="w-10 h-10 rounded-full flex items-center justify-center transition-transform active:scale-90" style={{ background: C.creamWarm }}>
              <Clock className="w-[18px] h-[18px]" style={{ color: C.forest }} />
            </Link>
            <button onClick={() => setCartOpen(true)} data-testid="shop-cart-button" className="relative w-10 h-10 rounded-full flex items-center justify-center transition-transform active:scale-90" style={{ background: C.forest }}>
              <ShoppingBag className="w-[18px] h-[18px]" style={{ color: C.cream }} />
              {cartCount > 0 && <span data-testid="shop-cart-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: C.gold, color: C.forest }}>{cartCount}</span>}
            </button>
          </div>
        </div>

        {banner && (
          <div data-testid="shop-banner" className="flex items-center gap-2 rounded-2xl px-4 py-3" style={{ background: banner.type === 'error' ? C.terracottaTint : C.leafTint }}>
            {banner.type === 'error' ? <AlertCircle className="w-4 h-4" style={{ color: C.terracotta }} /> : <CheckCircle2 className="w-4 h-4" style={{ color: C.leafDeep }} />}
            <span className="text-sm font-semibold flex-1" style={{ color: banner.type === 'error' ? C.terracotta : C.leafDeep }}>{banner.text}</span>
            {banner.library && <Link to="/learn" data-testid="banner-library-link" className="text-sm font-bold" style={{ color: C.leafDeep }}>Open ›</Link>}
            <button onClick={() => setBanner(null)}><X className="w-4 h-4" style={{ color: C.textMuted }} /></button>
          </div>
        )}

        {/* Member discount hint */}
        <div data-testid={isPremium ? 'member-active' : 'member-hint'} className="flex items-center gap-2.5 rounded-2xl px-4 py-3.5" style={{ background: isPremium ? C.leafDeep : C.forest, boxShadow: '0 14px 40px -20px rgba(26,67,49,0.5)' }}>
          <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(250,246,236,0.16)' }}>
            {isPremium ? <CheckCircle2 className="w-4 h-4" style={{ color: C.leaf }} /> : <Star className="w-4 h-4" style={{ color: C.gold }} />}
          </span>
          <span className="flex-1 font-semibold text-[13.5px]" style={{ color: C.cream }}>
            {isPremium ? '20% member discount applied to all Homegrown products.' : 'Members save 20% on every Homegrown product.'}
          </span>
          {!isPremium && (
            <button onClick={() => navigate('/subscription')} data-testid="member-hint-upgrade" className="rounded-full px-3.5 py-1.5 font-semibold text-sm" style={{ background: C.gold, color: C.forest }}>Get 20% off</button>
          )}
        </div>

        {showWishlist ? (
          <div data-testid="wishlist-view" className="pt-1">
            <div className="flex items-center gap-1.5 mb-0.5"><Heart className="w-4 h-4" style={{ color: C.terracotta }} /><span className="font-semibold" style={{ color: C.forest }}>Your wishlist</span></div>
            <p className="text-sm mb-3" style={{ color: C.textSoft }}>Products you&apos;ve saved to revisit</p>
            {wishlistProducts.length === 0 ? (
              <div className="text-center py-12 rounded-3xl" style={{ background: C.cream }}>
                <Heart className="w-10 h-10 mx-auto mb-3" style={{ color: C.sageDeep }} />
                <p className="mb-4" style={{ color: C.textSoft }}>Tap the heart on any product to save it here.</p>
                <button onClick={() => setShowWishlist(false)} className="rounded-full px-6 py-2.5 font-semibold" style={{ background: C.forest, color: C.cream }}>Browse products</button>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {wishlistProducts.map((p, i) => <ProductCard key={p.id} p={p} sym={sym} isPremium={isPremium} qtyInCart={productQty(p.id)} saved={wishlist.has(p.id)} onToggleSave={() => toggleWishlist(p.id)} onOpen={() => setDetail(p)} index={i} />)}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Recommended for You */}
            {recos.length > 0 && (
              <div data-testid="reco-section" className="pt-1">
                <div className="flex items-center gap-1.5 mb-0.5"><Zap className="w-4 h-4" style={{ color: C.gold }} /><span className="font-semibold" style={{ color: C.forest }}>Recommended for You</span></div>
                <p className="text-sm mb-3" style={{ color: C.textSoft }}>{recoCtx?.personalised ? 'Picked from your crops & pest alerts' : 'Grower favourites to get you started'}</p>
                <div className="flex gap-3 overflow-x-auto hg-scroll pb-2 -mx-1 px-1">
                  {recos.map((p) => <RecoCard key={p.id} p={p} sym={sym} isPremium={isPremium} saved={wishlist.has(p.id)} onToggleSave={() => toggleWishlist(p.id)} onOpen={() => setDetail(p)} />)}
                </div>
              </div>
            )}

            {/* Category chips */}
            <div className="flex gap-2 overflow-x-auto hg-scroll pb-1 -mx-1 px-1" data-testid="shop-categories">
              {cats.map((c) => (
                <button key={c.code} onClick={() => setActiveCat(c.code)} data-testid={`shop-cat-${c.code}`}
                  className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-all"
                  style={activeCat === c.code ? { background: C.forest, color: C.cream, borderColor: C.forest } : { background: C.cream, color: C.forest, borderColor: C.border }}>
                  {c.label}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Aesthetic product grid */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.6)', aspectRatio: '3 / 4' }} />)}
          </div>
        ) : !showWishlist ? (
          gridProducts.length === 0 ? (
            <div className="text-center py-16 rounded-3xl" style={{ background: C.cream }}>
              <ShoppingBag className="w-10 h-10 mx-auto mb-3" style={{ color: C.sageDeep }} />
              <p style={{ color: C.textSoft }}>No products in this category right now.</p>
            </div>
          ) : (
            <div data-testid="shop-grid" className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {gridProducts.map((p, i) => <ProductCard key={p.id} p={p} sym={sym} isPremium={isPremium} qtyInCart={productQty(p.id)} saved={wishlist.has(p.id)} onToggleSave={() => toggleWishlist(p.id)} onOpen={() => setDetail(p)} index={i} />)}
            </div>
          )
        ) : null}
      </div>

      {/* Floating cart bar — appears only when items are added */}
      <AnimatePresence>
        {cartCount > 0 && (
          <motion.div initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }} transition={{ type: 'spring', stiffness: 260, damping: 26 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-md px-1">
            <button onClick={() => setCartOpen(true)} data-testid="view-cart-bar" className="w-full flex items-center gap-3 rounded-full px-5 py-3.5" style={{ background: C.forest, boxShadow: '0 18px 40px -14px rgba(26,67,49,0.6)' }}>
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: C.gold, color: C.forest }}>{cartCount}</span>
              <span className="flex-1 text-left font-semibold" style={{ color: C.cream }}>View cart</span>
              <span className="font-semibold" style={{ color: C.cream }}>{money(sym, cartTotal)}</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {detail && (
          <ProductDetail key="detail" product={detail} sym={sym} isPremium={isPremium} lineQty={(vid) => cart.find((i) => i.product.id === detail.id && i.variant.id === vid)?.qty || 0}
            saved={wishlist.has(detail.id)} onToggleSave={() => toggleWishlist(detail.id)}
            onClose={() => setDetail(null)} onAdd={addLine} onDec={decLine} onViewCart={() => { setDetail(null); setCartOpen(true); }} />
        )}
        {cartOpen && (
          <CartSheet key="cart" cart={cart} sym={sym} isPremium={isPremium} total={cartTotal} needsShipping={needsShipping} checkingOut={checkingOut}
            onClose={() => setCartOpen(false)} onAdd={addLine} onDec={decLine} onCheckout={onCheckoutPress} />
        )}
        {shipOpen && (
          <ShippingForm key="ship" user={user} sym={sym} total={cartTotal} checkingOut={checkingOut} onClose={() => setShipOpen(false)} onSubmit={(addr) => doCheckout(addr)} />
        )}
      </AnimatePresence>
    </Layout>
  );
}

// ---- Product Detail: full-screen fade, hero zooms in, segmented format selector ----
function ProductDetail({ product, sym, isPremium, lineQty, saved, onToggleSave, onClose, onAdd, onDec, onViewCart }) {
  const [variant, setVariant] = useState(product.variants[0]);
  const qty = lineQty(variant.id);
  const showStrike = isPremium && product.discount_eligible && variant.effective_price < variant.price;
  const pillLabel = (v) => product.is_gift_card ? money(sym, v.effective_price) : (v.label || (v.is_digital ? 'Digital' : 'Physical'));

  return (
    <motion.div className="fixed inset-0 z-[70] flex flex-col" style={{ background: C.cream }} data-testid="product-detail"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.22 }}>
      {/* Hero expands in */}
      <motion.div className="relative w-full flex-shrink-0" style={{ height: 340 }} initial={{ scale: 1.06, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 180, damping: 22 }}>
        <img src={product.image} alt={product.name} className="w-full h-full object-cover" style={{ background: C.sageDeep }} />
        <button onClick={onClose} data-testid="detail-close" className="absolute top-4 left-4 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(250,246,236,0.92)', boxShadow: '0 6px 16px -8px rgba(26,67,49,0.35)' }}><ChevronDown className="w-5 h-5" style={{ color: C.forest }} /></button>
        <button onClick={onToggleSave} data-testid={`wishlist-heart-${product.id}`} className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(250,246,236,0.92)', boxShadow: '0 6px 16px -8px rgba(26,67,49,0.35)' }}><Heart className="w-5 h-5" style={{ color: saved ? C.terracotta : C.forest, fill: saved ? C.terracotta : 'none' }} /></button>
      </motion.div>

      <motion.div className="flex-1 flex flex-col -mt-7 rounded-t-[28px] overflow-hidden" style={{ background: C.cream }} initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.09, type: 'spring', stiffness: 200, damping: 24 }}>
        <div className="flex-1 overflow-y-auto px-6 pt-7 pb-6 max-w-2xl w-full mx-auto">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide flex-1" style={{ color: C.textSoft }}>{product.category_label}{product.brand ? ` · ${product.brand}` : ''}</span>
            {isPremium && product.discount_eligible && <SketchyBadge pct={product.discount_pct} />}
          </div>
          <h2 className="text-2xl font-bold mt-2" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>{product.name}</h2>
          {product.tagline && <p className="mt-2" style={{ color: C.textSoft }}>{product.tagline}</p>}
          {product.description && <p className="mt-4 leading-relaxed" style={{ color: C.text }}>{product.description}</p>}

          {product.variants.length > 1 && (
            <div data-testid="format-segment" className="flex gap-2 mt-6 p-1 rounded-full" style={{ background: C.sageDeep }}>
              {product.variants.map((v) => {
                const on = v.id === variant.id;
                return (
                  <button key={v.id} onClick={() => setVariant(v)} data-testid={`format-pill-${v.id}`} className="flex-1 py-2.5 rounded-full text-sm font-semibold transition-all" style={on ? { background: C.forest, color: C.cream } : { color: C.forest }}>
                    {pillLabel(v)}
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex items-center gap-2 mt-6 text-sm" style={{ color: C.textSoft }}>
            {variant.is_digital ? <Download className="w-3.5 h-3.5" /> : <Truck className="w-3.5 h-3.5" />}
            <span className="flex-1">
              {product.is_gift_card ? 'Instant code delivered to your Library'
                : variant.is_digital ? `Instant download · Includes ${(variant.files || []).map((f) => f.toUpperCase()).join(' + ') || 'PDF + EPUB'}`
                : 'Ships to your address'}
            </span>
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={variant.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.22 }} className="flex items-end gap-2.5 mt-3">
              <span className="text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }} data-testid="detail-price">{money(sym, variant.effective_price)}</span>
              {showStrike && <span className="text-lg line-through mb-1.5" style={{ color: C.textMuted }}>{money(sym, variant.price)}</span>}
              {showStrike && <span className="rounded-md px-1.5 py-0.5 text-xs font-bold mb-2" style={{ background: C.terracotta, color: C.cream }}>−{product.discount_pct}%</span>}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Bottom-docked wide CTA */}
        <div className="px-6 pt-3 pb-6 max-w-2xl w-full mx-auto" style={{ background: C.cream }}>
          {qty <= 0 ? (
            <PayPill label={product.is_gift_card ? 'Add gift card' : 'Add to cart'} icon={ShoppingBag} onClick={() => onAdd(product, variant)} testId="add-to-cart" />
          ) : (
            <div className="flex items-center gap-3">
              <Stepper qty={qty} onAdd={() => onAdd(product, variant)} onDec={() => onDec(product, variant)} id={`detail-${product.id}-${variant.id}`} />
              <div className="flex-1"><PayPill label="View cart" icon={ArrowRight} onClick={onViewCart} testId="detail-view-cart" /></div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ---- Single-page cart / checkout sheet with Apple Pay / Express-Pay ----
function CartSheet({ cart, sym, isPremium, total, needsShipping, checkingOut, onClose, onAdd, onDec, onCheckout }) {
  const [yearSaved, setYearSaved] = useState(null);
  useEffect(() => { axios.get(`${API}/shop/savings`, { withCredentials: true }).then((r) => setYearSaved(r.data)).catch(() => {}); }, []);
  const savings = cart.reduce((s, i) => (isPremium && i.product.discount_eligible) ? s + (i.variant.price - i.variant.effective_price) * i.qty : s, 0);

  return (
    <motion.div className="fixed inset-0 z-[70] flex items-end justify-center" style={{ background: 'rgba(15,45,31,0.55)' }} onClick={onClose}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div onClick={(e) => e.stopPropagation()} data-testid="cart-modal" className="w-full max-w-md rounded-t-[28px] px-5 pt-3 pb-8 max-h-[90vh] overflow-y-auto" style={{ background: C.cream }}
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', stiffness: 260, damping: 30 }}>
        <div className="w-11 h-1.5 rounded-full mx-auto mb-4" style={{ background: 'rgba(26,67,49,0.18)' }} />
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>Your cart</h3>
          <button onClick={onClose} data-testid="cart-close"><X className="w-5 h-5" style={{ color: C.textMuted }} /></button>
        </div>

        {yearSaved?.eligible && yearSaved.saved > 0 && (
          <div data-testid="year-savings-banner" className="flex items-center gap-2.5 rounded-2xl px-3 py-3 mb-3" style={{ background: C.leafDeep }}>
            <span className="w-[30px] h-[30px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: C.gold }}><PiggyBank className="w-4 h-4" style={{ color: C.forest }} /></span>
            <span className="flex-1 font-semibold text-[13.5px]" style={{ color: C.cream }}>You&rsquo;ve saved {money(sym, yearSaved.saved)} this year with membership 🎉</span>
          </div>
        )}

        {cart.length === 0 ? (
          <div className="text-center py-10"><ShoppingBag className="w-7 h-7 mx-auto" style={{ color: 'rgba(26,67,49,0.18)' }} /><p className="text-sm mt-2" style={{ color: C.textSoft }}>Your cart is empty.</p></div>
        ) : (
          <>
            <div className="max-h-[300px] overflow-y-auto">
              {cart.map((i) => (
                <div key={`${i.product.id}::${i.variant.id}`} data-testid={`cart-item-${i.product.id}`} className="flex items-center gap-3 py-3">
                  <img src={i.product.image} alt="" className="w-[52px] h-[52px] rounded-xl object-cover" style={{ background: C.sageDeep }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: C.forest }}>{i.product.name}</p>
                    <p className="text-xs" style={{ color: C.textSoft }}>{i.variant.label ? `${i.variant.label} · ` : ''}{money(sym, i.variant.effective_price)}{i.variant.label ? '' : ' each'}</p>
                    <p className="text-xs" style={{ color: i.variant.requires_shipping ? C.textMuted : C.leafDeep }}>{i.variant.requires_shipping ? 'Ships to you' : 'Instant'}</p>
                  </div>
                  <Stepper qty={i.qty} onAdd={() => onAdd(i.product, i.variant)} onDec={() => onDec(i.product, i.variant)} id={`cart-${i.product.id}-${i.variant.id}`} />
                </div>
              ))}
            </div>

            {isPremium && savings > 0 && (
              <div className="flex items-center justify-between mt-3">
                <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold" style={{ background: C.goldTint, color: '#7A5E1F' }}><Star className="w-3 h-3" /> Member −20%</span>
                <span className="text-sm font-semibold" style={{ color: C.leafDeep }}>You save {money(sym, savings)}</span>
              </div>
            )}
            {needsShipping && (
              <div className="flex items-center gap-1.5 mt-3 text-sm" style={{ color: C.textSoft }}><Truck className="w-3.5 h-3.5" /> We&apos;ll confirm your shipping address next</div>
            )}

            <div className="flex items-center justify-between my-3">
              <span className="font-semibold" style={{ color: C.forest }}>Total</span>
              <span className="text-2xl font-bold" data-testid="cart-total" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>{money(sym, total)}</span>
            </div>
            <PayPill apple label={`Pay  ${money(sym, total)}`} loading={checkingOut} onClick={onCheckout} testId="express-checkout" />
            <button onClick={onCheckout} disabled={checkingOut} data-testid="checkout-button" className="w-full text-center py-3 font-semibold text-sm" style={{ color: C.forest }}>
              {needsShipping ? 'Continue with card & shipping' : 'Pay with card instead'}
            </button>
            <div className="flex items-center gap-1.5 justify-center mt-1 text-xs" style={{ color: C.textSoft }}><Lock className="w-3 h-3" /> Secure payment via Stripe · Apple Pay on device</div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

// ---- Single-page shipping form with a sticky Apple Pay / Express-Pay ----
function ShippingForm({ user, sym, total, checkingOut, onSubmit, onClose }) {
  const [f, setF] = useState({ name: user?.name || '', line1: '', line2: '', city: '', region: '', postcode: '', country: user?.country || 'New Zealand' });
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const fields = [['name', 'Full name'], ['line1', 'Address line 1'], ['line2', 'Address line 2 (optional)'], ['city', 'City / town'], ['region', 'Region / state (optional)'], ['postcode', 'Postcode'], ['country', 'Country']];
  const submit = () => {
    for (const k of ['name', 'line1', 'city', 'postcode', 'country']) if (!String(f[k]).trim()) { setErr('Please complete all required fields.'); return; }
    setErr(''); onSubmit(f);
  };
  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end justify-center" style={{ background: 'rgba(15,45,31,0.55)' }} onClick={onClose}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div onClick={(e) => e.stopPropagation()} data-testid="shipping-form" className="w-full max-w-md rounded-t-[28px] px-5 pt-3 pb-8 flex flex-col" style={{ background: C.cream, maxHeight: '88vh' }}
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', stiffness: 260, damping: 30 }}>
        <div className="w-11 h-1.5 rounded-full mx-auto mb-4" style={{ background: 'rgba(26,67,49,0.18)' }} />
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>Shipping address</h3>
          <button onClick={onClose} data-testid="shipping-close"><X className="w-5 h-5" style={{ color: C.textMuted }} /></button>
        </div>
        <p className="text-sm mb-3" style={{ color: C.textSoft }}>Where should we send your order?</p>
        <div className="overflow-y-auto flex-1">
          {fields.map(([k, ph]) => (
            <input key={k} value={f[k]} onChange={set(k)} placeholder={ph} data-testid={`ship-${k}`}
              className="w-full px-4 py-3.5 mb-2 rounded-2xl outline-none text-[15px]" style={{ background: '#FFFFFF', border: `1px solid ${C.border}`, color: C.text }} />
          ))}
          {err && <p className="text-sm font-semibold mt-1" style={{ color: C.terracotta }}>{err}</p>}
          <div className="flex items-center justify-between my-3">
            <span className="font-semibold" style={{ color: C.forest }}>Total</span>
            <span className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>{money(sym, total)}</span>
          </div>
        </div>
        <div className="pt-3"><PayPill apple label={`Pay  ${money(sym, total)}`} loading={checkingOut} onClick={submit} testId="pay-button" /></div>
      </motion.div>
    </motion.div>
  );
}
